import React,{ forwardRef, useMemo, useCallback } from 'react';
import { BaseBlockProps, BarChartDataItem } from '../types';
import { useDateStatistics, ListFieldData, DefFieldList } from '../../../hooks';
import { getSchemeOrDefault } from '../../../utils';
import {
    BlockWrapper,
    BlockHeader,
    BlockToolbar,
    LoadingState,
    ChartContainer,
} from '../base';
import { BarChart } from '../../../Components/Display';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface DateStatisticsBlockProps extends BaseBlockProps {}

interface DateStatisticsDataItem {
    period: string | null;
    def_field_list_id?: number;
    total_items?: number;
    [key: string]: unknown;
}

export const DateStatisticsBlock = forwardRef<HTMLDivElement, DateStatisticsBlockProps>(
    (
        {
            kedo,
            block,
            dashboard,
            container,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            link,
        },
        ref
    ) => {
        const { data, listField, loading, setDateFormat, refetch } = useDateStatistics({
            kedo,
            block,
            dossier,
            link,
        });

        const handleFilterChange = useCallback(
            (key: string, value: string) => {
                if (key === 'dateFormat') {
                    setDateFormat(value);
                }
            },
            [setDateFormat]
        );

        const transFormData = useCallback(
            (rawData: DateStatisticsDataItem[], listFieldData: ListFieldData): BarChartDataItem[] => {
                const defFieldLists = listFieldData.def_field.def_field_lists;
                const dates: string[] = [];

                for (const item of rawData) {
                    if (item.period !== null && !dates.includes(item.period.toString())) {
                        dates.push(item.period.toString());
                    }
                }

                const results: BarChartDataItem[] = [];

                for (const date of dates) {
                    const newResult: BarChartDataItem = { period: date };

                    for (const defFieldList of defFieldLists) {
                        const keyName = kedo.env().translateItem(defFieldList, 'def_field_list');
                        const keyColor = defFieldList.color ? `${keyName}Color` : null;

                        let periodTotal = 0;
                        for (const dataItem of rawData) {
                            if (
                                dataItem.period !== null &&
                                dataItem.period.toString() === date &&
                                parseInt(String(dataItem.def_field_list_id)) === defFieldList.id
                            ) {
                                periodTotal = dataItem.total_items || 0;
                                break;
                            }
                        }

                        newResult[keyName] = periodTotal;
                        if (keyColor) {
                            newResult[keyColor] = defFieldList.color || null;
                        }
                    }

                    results.push(newResult);
                }

                return results;
            },
            [kedo]
        );

        const getKeys = useCallback(
            (listFieldData: ListFieldData | null): string[] => {
                if (!listFieldData) {
                    return ['total_items'];
                }

                return listFieldData.def_field.def_field_lists.map((item: DefFieldList) =>
                    kedo.env().translateItem(item, 'def_field_list')
                );
            },
            [kedo]
        );

        const chartData = useMemo((): BarChartDataItem[] => {
            if (!data || !Array.isArray(data)) return [];

            const typedData = data as DateStatisticsDataItem[];

            if (listField) {
                return transFormData(typedData, listField);
            }

            return typedData.map((item) => ({
                period: item.period || String((item as Record<string, unknown>).yearAnddate || ''),
                total_items: item.total_items || Number(item['1']) || 0,
            }));
        }, [data, listField, transFormData]);

        const chartKeys = useMemo(() => getKeys(listField), [listField, getKeys]);

        const getBarColor = useCallback(
            ({ id, data: barData }: { id: string; data: Record<string, unknown> }) => {
                const colorKey = `${id}Color`;
                if (barData[colorKey]) {
                    return String(barData[colorKey]);
                }
                return '#1f77b4';
            },
            []
        );

        const scheme = getSchemeOrDefault(block.settings?.scheme);
        const colors = listField ? getBarColor : { scheme };

        return (
            <BlockWrapper
                ref={ref}
                dashboard={dashboard}
                height={container ? '300px' : '100%'}
                sx={{
                    marginBottom: '30px',
                    minHeight: '300px',
                    minWidth: '300px',
                }}
            >
                <BlockHeader>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={refetch}
                        setFilterAndFetchData={handleFilterChange}
                    />
                    {getBlockHeader?.(block, dossier)}
                </BlockHeader>
                {loading ? (
                    <LoadingState />
                ) : (
                    <ChartContainer height="100%">
                        <BarChart
                            data={chartData}
                            keys={chartKeys}
                            indexBy="period"
                            colors={colors}
                            kedo={kedo}
                        />
                    </ChartContainer>
                )}
            </BlockWrapper>
        );
    }
);

DateStatisticsBlock.displayName = 'DateStatisticsBlock';
